import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext.js'
import './BarrierControls.css'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'
const REFRESH_MS = 15000

const ACTION_LABELS = { open: 'Auf', close: 'Zu', stop: 'Stopp' }
const DONE_LABELS   = { open: 'Auf gesendet', close: 'Zu gesendet', stop: 'Stopp gesendet' }

const hhmm = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// Who triggered the last command, in words staff recognise.
function describeActor(by, source) {
  if (source === 'kiosk')    return 'Terminal'
  if (source === 'camera')   return 'Kamera'
  if (source === 'schedule') return 'automatisch'
  return by || ''
}

function describeState(barrier) {
  const s = barrier.state
  if (!s?.known) return 'Zustand unbekannt'
  const who = describeActor(s.by, s.source)
  if (barrier.staysOpen && s.isOpen) return `Offen seit ${hhmm(s.at)}${who ? ` · ${who}` : ''}`
  return `Zuletzt ${ACTION_LABELS[s.lastCommand] || s.lastCommand} um ${hhmm(s.at)}${who ? ` · ${who}` : ''}`
}

/**
 * Auf / Zu / Stopp for the barriers a tenant may operate.
 *
 * Self-contained so it can sit on the Disposition now and on the Zufahrt page later
 * (plan phase 2). Renders nothing unless the tenant has the feature and at least one
 * barrier is switched on for the dashboard.
 *
 * Only the buttons a barrier can actually carry out are shown — that list comes from
 * the server, derived from how the barrier is wired.
 *
 * @param {string} tenantId
 * @param {{sessionId: string, name: string}|null} visitor  the visitor picked on the
 *   board, if any — the action is logged against their session.
 */
export default function BarrierControls({ tenantId, visitor = null }) {
  const { apiFetch, getToken, account } = useAuth()
  const [barriers, setBarriers] = useState([])
  const [busy, setBusy]         = useState(null)   // barrierId while a request runs
  const [messages, setMessages] = useState({})     // barrierId -> { kind, text }

  const load = useCallback(async () => {
    if (!tenantId) { setBarriers([]); return }
    try {
      const res = await apiFetch(`/api/admin/barriers/${tenantId}`)
      if (!res.ok) { setBarriers([]); return }
      const data = await res.json()
      setBarriers(data.enabled ? data.barriers || [] : [])
    } catch {
      /* transient — the next refresh catches up */
    }
  }, [tenantId, apiFetch])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    const id = setInterval(load, REFRESH_MS)
    return () => clearInterval(id)
  }, [load])

  // Another user, the terminal, the camera or the scheduler changed a barrier — update
  // that one in place so the Auf-lock and the state line are right immediately.
  useEffect(() => {
    if (!account || !tenantId) return
    let active = true
    let es = null
    let retry = null

    async function connect() {
      if (!active) return
      let token
      try { token = await getToken() } catch { retry = setTimeout(connect, 5000); return }
      if (!active) return
      es = new EventSource(`${BACKEND_URL}/api/admin/events?token=${token}`)
      es.addEventListener('barrier-state', (e) => {
        try {
          const data = JSON.parse(e.data)
          if (data.tenantId !== tenantId) return
          setBarriers(prev => prev.map(b => (b.id === data.barrierId ? { ...b, state: data.state } : b)))
        } catch { /* ignore a malformed event */ }
      })
      es.onerror = () => { es.close(); if (active) retry = setTimeout(connect, 5000) }
    }
    connect()

    return () => {
      active = false
      if (retry) clearTimeout(retry)
      es?.close()
    }
  }, [account, tenantId, getToken])

  const showMessage = (barrierId, kind, text) => {
    setMessages(prev => ({ ...prev, [barrierId]: { kind, text } }))
    setTimeout(() => setMessages(prev => {
      if (prev[barrierId]?.text !== text) return prev
      const next = { ...prev }
      delete next[barrierId]
      return next
    }), 6000)
  }

  const run = async (barrier, action, { force = false } = {}) => {
    if (action === 'close' && !window.confirm(`Ist die Durchfahrt frei?\n\n„${barrier.label}" jetzt schließen?`)) return
    if (force && !window.confirm(
      `„${barrier.label}" ist laut letztem Befehl bereits offen.\n\n` +
      'Nur fortfahren, wenn sie inzwischen von Hand geschlossen wurde. Trotzdem öffnen?'
    )) return

    setBusy(barrier.id)
    try {
      const res = await apiFetch(`/api/admin/barriers/${barrier.id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action, force, sessionId: visitor?.sessionId ?? null }),
      })
      const data = await res.json().catch(() => ({}))
      if (data.state) setBarriers(prev => prev.map(b => (b.id === barrier.id ? { ...b, state: data.state } : b)))

      if (res.ok && data.result === 'unknown') {
        showMessage(barrier.id, 'warn', 'Status unklar — Befehl gesendet, aber keine Antwort. Bitte Schranke prüfen.')
      } else if (res.ok) {
        showMessage(barrier.id, 'ok', DONE_LABELS[action])
      } else {
        showMessage(barrier.id, 'error', data.error || 'Aktion fehlgeschlagen')
        if (data.locked) load()
      }
    } catch {
      showMessage(barrier.id, 'error', 'Server nicht erreichbar')
    } finally {
      setBusy(null)
    }
  }

  if (barriers.length === 0) return null
  const showStation = new Set(barriers.map(b => b.stationName)).size > 1

  return (
    <div className="card bc-card">
      <div className="section-title bc-title">
        Schranken
        {visitor && <span className="bc-visitor">für {visitor.name || 'ausgewählten Besucher'}</span>}
      </div>
      <div className="bc-list">
        {barriers.map(b => {
          const locked = b.lockOpenUntilClosed && b.state?.isOpen
          const msg = messages[b.id]
          return (
            <div key={b.id} className={`bc-row${b.state?.isOpen ? ' bc-row--open' : ''}`}>
              <div className="bc-info">
                <span className="bc-label">{b.label}</span>
                {showStation && <span className="bc-station">{b.stationName}</span>}
                <span className="bc-state">{describeState(b)}</span>
                {msg && <span className={`bc-msg bc-msg--${msg.kind}`}>{msg.text}</span>}
              </div>
              <div className="bc-actions">
                {b.actions.includes('open') && (
                  <button
                    className="btn btn-primary bc-btn"
                    disabled={busy === b.id || locked}
                    title={locked ? 'Bereits geöffnet — erst „Zu" drücken' : undefined}
                    onClick={() => run(b, 'open')}
                  >
                    {ACTION_LABELS.open}
                  </button>
                )}
                {b.actions.includes('close') && (
                  <button className="btn btn-ghost bc-btn" disabled={busy === b.id} onClick={() => run(b, 'close')}>
                    {ACTION_LABELS.close}
                  </button>
                )}
                {b.actions.includes('stop') && (
                  <button className="btn bc-btn bc-stop" disabled={busy === b.id} onClick={() => run(b, 'stop')}>
                    {ACTION_LABELS.stop}
                  </button>
                )}
                {busy === b.id && <span className="spinner spinner-sm" />}
                {locked && busy !== b.id && (
                  <button className="bc-force" onClick={() => run(b, 'open', { force: true })}>
                    Trotzdem öffnen
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
